/**
 * Optimized Imports Utility
 *
 * Provides tree-shakable imports and utilities for reducing bundle size
 * through selective importing and dead code elimination.
 */

import * as React from 'react';

// Re-export only needed OAuth functions
export {
  AuthProvider,
  type TAuthConfig,
  type TRefreshTokenExpiredEvent,
} from '@sangoma/react-oauth2-code-pkce';
// Re-export only needed React Query functions
export {
  QueryClient,
  QueryClientProvider,
  type UseMutationResult,
  type UseQueryResult,
  useMutation,
  useQuery,
} from '@tanstack/react-query';
// Re-export only what we need from React
export {
  Component,
  type ComponentType,
  createContext,
  type FC,
  forwardRef,
  lazy,
  memo,
  type ReactNode,
  type RefObject,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

// Lazy loading factory (use in components with React hooks)
export const createLazyComponentFactory = <T extends object>(
  importFn: () => Promise<{ default: React.ComponentType<T> }>,
) => {
  return React.lazy(importFn);
};

// Tree-shakable utility functions
// biome-ignore lint/suspicious/noExplicitAny: Generic function parameter types need any[] for flexibility
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number,
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

// biome-ignore lint/suspicious/noExplicitAny: Generic function parameter types need any[] for flexibility
export const throttle = <T extends (...args: any[]) => any>(
  func: T,
  limit: number,
): ((...args: Parameters<T>) => void) => {
  let inThrottle: boolean;
  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => {
        inThrottle = false;
      }, limit);
    }
  };
};

// Optimized class name utility (smaller than classnames package)
export const cn = (...classes: (string | undefined | null | false)[]): string => {
  return classes.filter(Boolean).join(' ');
};

// Environment utilities (browser-safe)
export const isDev = (() => {
  try {
    return typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'development';
  } catch {
    return typeof window !== 'undefined' && window.location.hostname === 'localhost';
  }
})();

export const isProd = (() => {
  try {
    return typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'production';
  } catch {
    return typeof window !== 'undefined' && window.location.hostname !== 'localhost';
  }
})();

export const isTest = (() => {
  try {
    return typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'test';
  } catch {
    return false;
  }
})();

// Performance utilities
export const measurePerformance = <T>(name: string, fn: () => T): T => {
  if (!isDev) {
    return fn();
  }

  const start = performance.now();
  const result = fn();
  const end = performance.now();
  console.log(`⚡ ${name}: ${(end - start).toFixed(2)}ms`);
  return result;
};

// Type-safe environment variable getter (browser-safe)
export const getEnv = (key: string, defaultValue?: string): string => {
  let value: string | undefined;

  try {
    const metaEnv = (import.meta.env ?? {}) as Record<string, string | undefined>;
    value =
      metaEnv[key] ||
      (typeof process !== 'undefined' && process.env ? process.env[key] : undefined);
  } catch {
    value = undefined;
  }

  if (value === undefined && defaultValue === undefined) {
    throw new Error(`Environment variable ${key} is not defined`);
  }
  return value || defaultValue || '';
};

// Optimized array utilities
export const uniqueBy = <T>(array: T[], key: keyof T): T[] => {
  const seen = new Set();
  return array.filter((item) => {
    const value = item[key];
    if (seen.has(value)) {
      return false;
    }
    seen.add(value);
    return true;
  });
};

export const groupBy = <T>(array: T[], key: keyof T): Record<string, T[]> => {
  return array.reduce(
    (groups, item) => {
      const value = String(item[key]);
      groups[value] = groups[value] || [];
      groups[value].push(item);
      return groups;
    },
    {} as Record<string, T[]>,
  );
};

// Optimized object utilities
export const pick = <T extends object, K extends keyof T>(obj: T, keys: K[]): Pick<T, K> => {
  const result = {} as Pick<T, K>;
  for (const key of keys) {
    if (key in obj) {
      result[key] = obj[key];
    }
  }
  return result;
};

export const omit = <T, K extends keyof T>(obj: T, keys: K[]): Omit<T, K> => {
  const result = { ...obj };
  for (const key of keys) {
    delete result[key];
  }
  return result;
};

export default {
  createLazyComponentFactory,
  debounce,
  throttle,
  cn,
  isDev,
  isProd,
  isTest,
  measurePerformance,
  getEnv,
  uniqueBy,
  groupBy,
  pick,
  omit,
};
